/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  TFeProvisioningAccountFeatureAttribute,
  TFeProvisioningAccountFeaturesPostParams
} from "@tivocorp/data-domain-lib";

import type { TParentalSettingsUpdate } from "../types";

/**
 * Serializes a {@link TParentalSettingsUpdate} into the
 * `POST /v2/accountFeatures` body. The write-side counterpart of
 * `ParentalSettingsParser`: values are written in the shapes the parser reads
 * back from `GET /v2/deviceFeatures`.
 */
export class ParentalSettingsSerializer {
  /** Global external name of the PC state attribute. */
  public static readonly STATE: string = "parentalSettings-state";

  /** Global external name of the Hide Adult Content attribute. */
  public static readonly HIDE_ADULT: string = "parentalSettings-hideAdult";

  /** Global external name of the rating limits attribute. */
  public static readonly RATING_LIMITS: string =
    "parentalSettings-ratingLimits";

  /**
   * Global external name of the PIN attribute. FE names it `hashedPin` but
   * takes the raw PIN on write and hashes it server-side.
   */
  public static readonly PIN: string = "parentalSettings-hashedPin";

  /**
   * Build the request body for a settings update.
   * @param update - The settings to change
   * @returns The `POST /v2/accountFeatures` body
   * @throws Error when the update changes nothing
   */
  public static toAccountFeaturesRequest(
    update: TParentalSettingsUpdate
  ): TFeProvisioningAccountFeaturesPostParams {
    const attributes: Array<TFeProvisioningAccountFeatureAttribute> = [];
    if (update.state !== undefined) {
      attributes.push({
        globalExternalName: ParentalSettingsSerializer.STATE,
        attributeValue: update.state
      });
    }
    if (update.hideAdult !== undefined) {
      attributes.push({
        globalExternalName: ParentalSettingsSerializer.HIDE_ADULT,
        attributeValue: String(update.hideAdult)
      });
    }
    if (update.ratingLimits !== undefined) {
      attributes.push({
        globalExternalName: ParentalSettingsSerializer.RATING_LIMITS,
        attributeValue: JSON.stringify(
          update.ratingLimits.map(({ ratingTypeId, ratingValueId }) => ({
            ratingTypeId,
            ratingValueId
          }))
        )
      });
    }
    if (update.pin !== undefined) {
      attributes.push({
        globalExternalName: ParentalSettingsSerializer.PIN,
        attributeValue: update.pin
      });
    }
    if (attributes.length === 0) {
      throw new Error("Parental settings update has no changes");
    }
    return { features: [{ attributes }] };
  }
}
